/**
 * Web-side mirror of `shared/src/util/college-projector.ts`. Web does
 * NOT import from `@hearth/shared` (locked since 5.1). Behavior must
 * match the shared implementation; matching tests at
 * `web/src/lib/util/college-projector.test.ts` are the contract.
 *
 * See `shared/src/util/college-projector.ts` for modeling notes.
 */

export interface ProjectCollegePlanInput {
  start_balance_cents: number;
  monthly_contribution_cents: number;
  growth_rate_pct: number;
  start_year: number;
  college_start_year: number;
  college_years: number;
  cost_today_cents: number;
  cost_inflation_pct: number;
}

export interface CollegePlanScheduleRow {
  year: number;
  balance_cents: number;
  projected_annual_cost_cents: number;
}

export interface ProjectCollegePlanResult {
  schedule: CollegePlanScheduleRow[];
  total_needed_cents: number;
  surplus_or_shortfall_cents: number;
  monthly_shortfall_cents: number;
}

export function projectCollegePlan(
  input: ProjectCollegePlanInput,
): ProjectCollegePlanResult {
  const {
    start_balance_cents,
    monthly_contribution_cents,
    growth_rate_pct,
    start_year,
    college_start_year,
    college_years,
    cost_today_cents,
    cost_inflation_pct,
  } = input;

  if (college_years < 1) {
    throw new Error(
      `projectCollegePlan: college_years must be ≥ 1, got ${college_years}`,
    );
  }
  if (college_start_year < start_year) {
    throw new Error(
      `projectCollegePlan: college_start_year (${college_start_year}) must be ≥ start_year (${start_year})`,
    );
  }
  if (start_balance_cents < 0) {
    throw new Error(
      `projectCollegePlan: start_balance_cents must be ≥ 0, got ${start_balance_cents}`,
    );
  }

  const growth = growth_rate_pct / 100;
  const inflation = cost_inflation_pct / 100;
  const annualContribution = monthly_contribution_cents * 12;
  const collegeEndYear = college_start_year + college_years;

  let balance = start_balance_cents;
  let totalNeeded = 0;
  const schedule: CollegePlanScheduleRow[] = [];

  for (let year = start_year; year < collegeEndYear; year += 1) {
    const yearsFromStart = year - start_year;
    const projectedAnnualCost = Math.round(
      cost_today_cents * Math.pow(1 + inflation, yearsFromStart),
    );

    if (year < college_start_year) {
      balance = Math.round((balance + annualContribution) * (1 + growth));
      schedule.push({
        year,
        balance_cents: balance,
        projected_annual_cost_cents: projectedAnnualCost,
      });
    } else {
      balance = Math.round(balance * (1 + growth));
      balance -= projectedAnnualCost;
      totalNeeded += projectedAnnualCost;
      schedule.push({
        year,
        balance_cents: balance,
        projected_annual_cost_cents: projectedAnnualCost,
      });
    }
  }

  const surplusOrShortfall = balance;
  let monthlyShortfall = 0;
  if (surplusOrShortfall < 0) {
    const yearsContributing = Math.max(1, college_start_year - start_year);
    const monthsContributing = yearsContributing * 12;
    monthlyShortfall = Math.ceil(-surplusOrShortfall / monthsContributing);
  }

  return {
    schedule,
    total_needed_cents: totalNeeded,
    surplus_or_shortfall_cents: surplusOrShortfall,
    monthly_shortfall_cents: monthlyShortfall,
  };
}
